/**
 * UdhyamRegistrationInput
 *
 * Displays the Udhyam Registration Number field.
 *
 * Enhanced behaviour (post-UdhyamFetch):
 *  - The getter (LOS_GetBusinessDetails_003) returns `udyam_number` (the first
 *    URN written by the Perfios fetch) and optionally `udhyam_details` (the
 *    full Perfios JSON with result.urn[] containing all URNs).
 *  - If multiple URNs were found, a read-only chip list is shown above the
 *    editable input so the user can see all options and confirm / override.
 *  - The editable input is pre-filled with `udyam_number` (first URN).
 *  - The component is still fully editable so the user can type a different
 *    number if needed.
 */

import React, { useCallback, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface UdhyamRegistrationInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: {
    udyam_number?: string | null;
    /** Full Perfios response stored by UdhyamFetch — optional */
    udhyam_details?: {
      result?: {
        urn?: string[];
        data?: any[];
      };
    } | null;
  };
  onChange?: (val: { udyam_number: string }) => void;
  disabled?: boolean;
  // Legacy pattern
  initialData?: { udyam_number?: string };
  onDataChange?: (data: { udyam_number: string }) => void;
}

const UdhyamRegistrationInput: React.FC<UdhyamRegistrationInputProps> = ({
  value,
  onChange,
  disabled,
  initialData,
  onDataChange,
}) => {
  const udyamNumber = (value?.udyam_number ?? initialData?.udyam_number ?? '') || '';

  /** All URNs returned by Perfios (may be empty if fetch hasn't run yet) */
  const allUrns: string[] = useMemo(() => {
    const urns = value?.udhyam_details?.result?.urn;
    return Array.isArray(urns) ? urns.filter(Boolean) : [];
  }, [value?.udhyam_details]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase();
    onChange?.({ udyam_number: val });
    onDataChange?.({ udyam_number: val });
  }, [onChange, onDataChange]);

  /** When the user clicks a chip, pre-fill the input with that URN */
  const handleChipClick = useCallback((urn: string) => {
    if (disabled) return;
    onChange?.({ udyam_number: urn });
    onDataChange?.({ udyam_number: urn });
  }, [disabled, onChange, onDataChange]);

  return (
    <div className="space-y-3">
      <Label htmlFor="udyam_number" className="text-gray-500 font-medium">
        Udhyam Registration Number <span className="text-gray-500">(Optional)</span>
      </Label>

      {/* ------------------------------------------------------------------ */}
      {/* Fetched URN chips — shown only when Perfios returned results         */}
      {/* ------------------------------------------------------------------ */}
      {allUrns.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-gray-400">
            {allUrns.length === 1
              ? 'Found via mobile number:'
              : `${allUrns.length} registrations found — tap to select:`}
          </p>
          <div className="flex flex-wrap gap-2">
            {allUrns.map((urn) => {
              const isSelected = urn === udyamNumber;
              return (
                <button
                  key={urn}
                  type="button"
                  onClick={() => handleChipClick(urn)}
                  disabled={disabled}
                  className={[
                    'px-3 py-1.5 rounded-lg text-xs font-mono border transition-all',
                    isSelected
                      ? 'bg-intellend-green text-white border-intellend-green shadow-sm'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-intellend-green hover:text-intellend-green',
                    disabled ? 'cursor-default opacity-70' : 'cursor-pointer',
                  ].join(' ')}
                >
                  {urn}
                  {isSelected && (
                    <span className="ml-1.5 opacity-80">✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Editable input — pre-filled with first URN, user can override       */}
      {/* ------------------------------------------------------------------ */}
      <Input
        id="udyam_number"
        type="text"
        value={udyamNumber}
        onChange={handleChange}
        placeholder="UDYAM-XX-00-0000000"
        disabled={disabled}
        className="border-gray-200 focus:border-intellend-green focus:ring-intellend-green rounded-xl py-6 uppercase"
      />

      {/* Helper text when no Perfios data is available yet */}
      {allUrns.length === 0 && !udyamNumber && (
        <p className="text-xs text-gray-400">
          Enter your Udhyam Registration Number (e.g. UDYAM-MH-19-0004749)
        </p>
      )}
    </div>
  );
};

export default UdhyamRegistrationInput;
